// Equality at work: the shared data behind the front door, the two hubs and
// the "Is it protected?" checker.
//
// Every legal point names the section it relies on, linked to the current
// revised text on legislation.gov.uk. Checked against the revised text on
// CHECKED_ON. When the law changes, change the entry here and the date.

export const CHECKED_ON = { iso: "2026-10-31", label: "31 October 2026" };

export const HUB_PATHS = {
  home: "/equality-at-work",
  employers: "/equality-at-work/employers",
  employees: "/equality-at-work/at-work",
  checker: "/equality-at-work/is-it-protected",
};

const EQA = (part) => `https://www.legislation.gov.uk/ukpga/2010/15/${part}`;
const DREGS = (reg) => `https://www.legislation.gov.uk/uksi/2010/2128/regulation/${reg}`;

// The nine protected characteristics, Equality Act 2010 s.4.
export const CHARACTERISTICS = [
  { name: "Age", section: "s.5", url: EQA("section/5"), plain: "Any age, young or old, or an age group such as \"over 50s\" or \"under 25s\"." },
  { name: "Disability", section: "s.6 and Sch 1", url: EQA("section/6"), plain: "A physical or mental impairment with a substantial and long-term effect on normal day-to-day activities, including taking part in working life. Cancer, HIV and multiple sclerosis count from diagnosis." },
  { name: "Gender reassignment", section: "s.7", url: EQA("section/7"), plain: "Proposing to undergo, undergoing or having undergone a process (or part of one) to reassign sex. No medical treatment is required." },
  { name: "Marriage and civil partnership", section: "s.8", url: EQA("section/8"), plain: "Being married or a civil partner. Being single, engaged or living together is not covered." },
  { name: "Pregnancy and maternity", section: "s.18 (work)", url: EQA("section/18"), plain: "Being pregnant, ill because of pregnancy, or on or taking maternity leave, during the protected period." },
  { name: "Race", section: "s.9", url: EQA("section/9"), plain: "Colour, nationality, and ethnic or national origins." },
  { name: "Religion or belief", section: "s.10", url: EQA("section/10"), plain: "Any religion or philosophical belief, and having no religion or belief." },
  { name: "Sex", section: "s.11", url: EQA("section/11"), plain: "Being a man or a woman. The Supreme Court held in 2025 that this means biological sex." },
  { name: "Sexual orientation", section: "s.12", url: EQA("section/12"), plain: "Orientation towards people of the same sex, the opposite sex, or either sex." },
];

// Verdicts used by the checker.
export const VERDICTS = {
  yes: { label: "Protected", tone: "yes" },
  maybe: { label: "Can be protected", tone: "maybe" },
  indirect: { label: "Often covered another way", tone: "maybe" },
  no: { label: "Not protected by the Equality Act", tone: "no" },
};

// The checker. Each situation: what people ask about, the verdict, the
// characteristic (if any), a plain answer, what a fair employer does either
// way, and the law it relies on.
export const SITUATIONS = [
  {
    id: "pregnancy",
    group: "Pregnancy and family",
    title: "Being pregnant, or on maternity leave",
    verdict: "yes",
    characteristic: "Pregnancy and maternity",
    answer: [
      "Yes. At work, the protection runs for the \"protected period\": from the start of the pregnancy to the end of maternity leave, or earlier return to work. Treating a woman unfavourably during that period because of the pregnancy, a pregnancy-related illness, or because she is on or taking maternity leave is discrimination. There is no need to compare her with anyone else.",
      "Unfavourable treatment because of pregnancy that happens after the protected period ends can still be sex discrimination.",
    ],
    bestPractice: "Do the pregnancy risk assessment straight away, keep her in pay reviews, bonuses and promotion rounds while she is away, and agree how you will keep in touch before her leave starts.",
    law: [
      { label: "Equality Act 2010, s.18", url: EQA("section/18") },
      { label: "Equality Act 2010, s.13", url: EQA("section/13") },
    ],
  },
  {
    id: "breastfeeding",
    group: "Pregnancy and family",
    title: "Breastfeeding",
    verdict: "yes",
    characteristic: "Sex",
    answer: [
      "Yes. The Act says less favourable treatment of a woman because she is breastfeeding counts as less favourable treatment because of sex.",
    ],
    bestPractice: "Provide somewhere private and clean (not a toilet) to express milk, and agree breaks in advance. It is cheap and it keeps people coming back.",
    law: [{ label: "Equality Act 2010, s.13(6)(a)", url: EQA("section/13") }],
  },
  {
    id: "childcare",
    group: "Pregnancy and family",
    title: "Childcare or other caring responsibilities",
    verdict: "indirect",
    characteristic: "Often sex, or disability by association",
    answer: [
      "Being a parent or carer is not a protected characteristic in itself. But a rigid rule, such as full-time hours only or no changes to start times, can be indirect sex discrimination if it puts women at a particular disadvantage and the employer cannot justify it. Tribunals often accept that women still carry more of the childcare.",
      "Treating someone badly because they care for a disabled person can be direct disability discrimination or harassment by association, even though the employee is not disabled.",
      "Separately, every employee can ask for flexible working from their first day.",
    ],
    bestPractice: "Judge requests for different hours on what the job actually needs, write down your reasons, and offer a trial period before saying no.",
    law: [
      { label: "Equality Act 2010, s.19", url: EQA("section/19") },
      { label: "Equality Act 2010, s.13", url: EQA("section/13") },
      { label: "Employment Rights Act 1996, s.80F", url: "https://www.legislation.gov.uk/ukpga/1996/18/section/80F" },
    ],
  },
  {
    id: "part-time",
    group: "Pregnancy and family",
    title: "Working part-time",
    verdict: "indirect",
    characteristic: "Separate part-time workers law; sometimes sex",
    answer: [
      "Part-time work is not covered by the Equality Act, but part-time workers have their own protection. They must not be treated less favourably than a comparable full-time worker because they are part-time, unless that is objectively justified, and pay and benefits should normally be pro rata.",
      "Because more women work part-time, treating part-timers worse can also be indirect sex discrimination.",
    ],
    bestPractice: "Pro rata everything: pay, holiday, bonus, training and promotion chances.",
    law: [
      { label: "Part-time Workers Regulations 2000, reg 5", url: "https://www.legislation.gov.uk/uksi/2000/1551/regulation/5" },
      { label: "Equality Act 2010, s.19", url: EQA("section/19") },
    ],
  },
  {
    id: "mental-health",
    group: "Health and disability",
    title: "A mental health condition, such as anxiety or depression",
    verdict: "maybe",
    characteristic: "Disability, if the legal test is met",
    answer: [
      "Mental health is not a protected characteristic on its own. It is covered when it is a disability: a mental impairment with a substantial and long-term adverse effect on normal day-to-day activities.",
      "\"Substantial\" only means more than minor or trivial. \"Long-term\" means it has lasted, or is likely to last, 12 months, or the rest of the person's life, and an effect that comes and goes counts if it is likely to recur. The effect is judged as it would be without treatment such as medication or therapy. For work cases, day-to-day activities include taking part fully and effectively in working life.",
    ],
    bestPractice: "Don't argue about the label. Ask what would help, write it down, and review it. Most adjustments for mental health cost little or nothing.",
    law: [
      { label: "Equality Act 2010, s.6", url: EQA("section/6") },
      { label: "Equality Act 2010, Sch 1 paras 2, 5 and 5A", url: EQA("schedule/1") },
      { label: "Equality Act 2010, s.212(1) (\"substantial\")", url: EQA("section/212") },
    ],
  },
  {
    id: "neurodivergence",
    group: "Health and disability",
    title: "Autism, ADHD, dyslexia or dyspraxia",
    verdict: "maybe",
    characteristic: "Disability, if the legal test is met",
    answer: [
      "Neurodivergent conditions are not listed in the Act, so a diagnosis alone does not settle it. They are covered when they meet the disability test: a substantial (more than minor or trivial) and long-term effect on normal day-to-day activities, which for work includes taking part fully and effectively in working life.",
      "Medical treatment and aids are ignored when judging the effect. Coping strategies the person has learned are different: they can reduce the effect, but the question is whether they hold up at work, for example under pressure or when routines change. Once someone is disabled, the employer must make reasonable adjustments, and must not treat them unfavourably because of something arising from their condition unless that is justified.",
    ],
    bestPractice: "Ask the person what gets in their way: written instructions, quieter desks, notice of changes, extra time. Government Access to Work funding can help pay for support.",
    law: [
      { label: "Equality Act 2010, s.6", url: EQA("section/6") },
      { label: "Equality Act 2010, Sch 1 para 5A", url: EQA("schedule/1") },
      { label: "Equality Act 2010, s.15", url: EQA("section/15") },
      { label: "Equality Act 2010, s.20", url: EQA("section/20") },
    ],
  },
  {
    id: "cancer-hiv-ms",
    group: "Health and disability",
    title: "Cancer, HIV or multiple sclerosis",
    verdict: "yes",
    characteristic: "Disability",
    answer: [
      "Yes, from the day of diagnosis. The Act treats each of these as a disability, so there is no need to show any effect on day-to-day activities.",
      "Someone who has had a disability in the past, for example cancer now in remission, is still protected.",
    ],
    bestPractice: "Talk early about treatment schedules, phased returns and adjustments, and don't count treatment-related absence the same way as ordinary sickness without thinking about it.",
    law: [
      { label: "Equality Act 2010, Sch 1 para 6", url: EQA("schedule/1") },
      { label: "Equality Act 2010, s.6(4)", url: EQA("section/6") },
    ],
  },
  {
    id: "long-term-illness",
    group: "Health and disability",
    title: "Long Covid, chronic fatigue or another long-term illness",
    verdict: "maybe",
    characteristic: "Disability, if the legal test is met",
    answer: [
      "Covered if it meets the disability test: a substantial and long-term adverse effect on normal day-to-day activities. Conditions that fluctuate count if the effect is likely to recur, and a progressive condition can count before the effect becomes substantial if it is likely to get there.",
    ],
    bestPractice: "Plan around good and bad days rather than a fixed return date, and review adjustments regularly.",
    law: [
      { label: "Equality Act 2010, s.6", url: EQA("section/6") },
      { label: "Equality Act 2010, Sch 1 paras 2 and 8", url: EQA("schedule/1") },
    ],
  },
  {
    id: "menopause",
    group: "Health and disability",
    title: "Menopause",
    verdict: "indirect",
    characteristic: "Often sex, age or disability",
    answer: [
      "Menopause is not a protected characteristic in its own right. Treatment linked to it can still be discrimination or harassment because of sex or age, and symptoms that meet the disability test can make someone disabled, which brings in the duty to make reasonable adjustments.",
      "Jokes and comments about menopause can be harassment related to sex or age.",
    ],
    bestPractice: "Treat it as a health issue with simple fixes: temperature control, flexibility over breaks and start times, uniform changes, and a manager who knows not to make it a joke.",
    law: [
      { label: "Equality Act 2010, ss.13 and 26", url: EQA("section/26") },
      { label: "Equality Act 2010, s.6", url: EQA("section/6") },
    ],
  },
  {
    id: "addiction",
    group: "Health and disability",
    title: "Addiction to alcohol or drugs",
    verdict: "no",
    characteristic: "Excluded, with exceptions",
    answer: [
      "Addiction to alcohol, nicotine or any other substance is not treated as an impairment, unless the addiction began as a result of medically prescribed drugs or other medical treatment.",
      "A condition caused by addiction, such as liver disease or depression, can still be a disability in its own right.",
    ],
    bestPractice: "Have a clear policy that offers support before discipline where someone asks for help.",
    law: [{ label: "Equality Act 2010 (Disability) Regulations 2010, reg 3", url: DREGS(3) }],
  },
  {
    id: "hay-fever",
    group: "Health and disability",
    title: "Hay fever",
    verdict: "no",
    characteristic: "Excluded, with one exception",
    answer: [
      "Seasonal hay fever is not treated as an impairment. It can be taken into account where it makes another condition worse, such as asthma.",
    ],
    bestPractice: "Small fixes, like a desk away from an open window, cost nothing.",
    law: [{ label: "Equality Act 2010 (Disability) Regulations 2010, reg 4", url: DREGS(4) }],
  },
  {
    id: "appearance",
    group: "Health and disability",
    title: "Weight, appearance or tattoos",
    verdict: "no",
    characteristic: "Not on its own",
    answer: [
      "Weight and appearance are not protected characteristics. A condition linked to weight can be a disability if it meets the disability test. A severe disfigurement is treated as a disability, but tattoos and decorative piercings are expressly excluded from that rule.",
      "Dress and appearance rules can still be discriminatory if they treat men and women differently in a way that disadvantages one group, or clash with religious dress, unless justified.",
    ],
    bestPractice: "Base dress codes on what the job needs, and apply the same standard to everyone.",
    law: [
      { label: "Equality Act 2010, Sch 1 para 3", url: EQA("schedule/1") },
      { label: "Equality Act 2010 (Disability) Regulations 2010, reg 5", url: DREGS(5) },
    ],
  },
  {
    id: "sex",
    group: "Who you are",
    title: "Being a woman, or being a man",
    verdict: "yes",
    characteristic: "Sex",
    answer: [
      "Yes, for women and men alike. In April 2025 the Supreme Court held that \"sex\" in the Equality Act means biological sex (For Women Scotland Ltd v The Scottish Ministers [2025] UKSC 16).",
      "Sexual harassment, and harassment related to sex, are unlawful separately from direct discrimination, and employers must take all reasonable steps to prevent sexual harassment of their employees.",
    ],
    bestPractice: "Check your pay, promotion and flexible working decisions for patterns by sex, not just individual cases.",
    law: [
      { label: "Equality Act 2010, s.11", url: EQA("section/11") },
      { label: "Equality Act 2010, s.26", url: EQA("section/26") },
      { label: "For Women Scotland, Supreme Court judgment", url: "https://www.supremecourt.uk/cases/uksc-2024-0042" },
    ],
  },
  {
    id: "gender-reassignment",
    group: "Who you are",
    title: "Being trans, or transitioning",
    verdict: "yes",
    characteristic: "Gender reassignment",
    answer: [
      "Yes. The protection covers anyone proposing to undergo, undergoing or having undergone a process, or part of a process, of reassigning their sex. Medical treatment is not required, and someone who has only proposed to transition is covered.",
    ],
    bestPractice: "Agree a plan with the person about time off, records and how colleagues are told, and deal with harassment quickly.",
    law: [{ label: "Equality Act 2010, s.7", url: EQA("section/7") }],
  },
  {
    id: "sexual-orientation",
    group: "Who you are",
    title: "Being gay, lesbian, bisexual or straight",
    verdict: "yes",
    characteristic: "Sexual orientation",
    answer: ["Yes. Orientation towards the same sex, the opposite sex, or either sex is protected."],
    bestPractice: "Treat \"banter\" about someone's orientation as the harassment risk it is.",
    law: [{ label: "Equality Act 2010, s.12", url: EQA("section/12") }],
  },
  {
    id: "age",
    group: "Who you are",
    title: "Being young, or being older",
    verdict: "yes",
    characteristic: "Age",
    answer: [
      "Yes, at any age. Age is the one characteristic where even direct discrimination can be lawful, if the employer can show it is a proportionate means of achieving a legitimate aim. That is a high bar.",
    ],
    bestPractice: "Keep age out of job adverts (\"young and dynamic\", \"recent graduate\") and retirement conversations unless the person raises it.",
    law: [
      { label: "Equality Act 2010, s.5", url: EQA("section/5") },
      { label: "Equality Act 2010, s.13(2)", url: EQA("section/13") },
    ],
  },
  {
    id: "race",
    group: "Who you are",
    title: "Race, colour, nationality or ethnic origin",
    verdict: "yes",
    characteristic: "Race",
    answer: [
      "Yes. Race includes colour, nationality, and ethnic or national origins.",
      "Caste is not yet expressly listed: the Act requires the government to add it by order, and that order has not been made. Depending on the facts, a caste-related claim may still fall within ethnic origins.",
    ],
    bestPractice: "Use the same right-to-work checks for every candidate, whatever their accent or name.",
    law: [{ label: "Equality Act 2010, s.9", url: EQA("section/9") }],
  },
  {
    id: "accent",
    group: "Who you are",
    title: "Accent",
    verdict: "indirect",
    characteristic: "Often race",
    answer: [
      "Accent is not listed, but it is often closely linked to nationality or national or ethnic origin, so treating someone badly because of their accent can be race discrimination or harassment.",
    ],
    bestPractice: "If clear communication matters for the job, test the actual skill, not the accent.",
    law: [{ label: "Equality Act 2010, s.9", url: EQA("section/9") }],
  },
  {
    id: "religion",
    group: "Who you are",
    title: "Religion, or having no religion",
    verdict: "yes",
    characteristic: "Religion or belief",
    answer: ["Yes. Any religion is covered, and so is having no religion."],
    bestPractice: "Handle requests for prayer time, religious dress or time off for festivals on their merits, the same way each time.",
    law: [{ label: "Equality Act 2010, s.10", url: EQA("section/10") }],
  },
  {
    id: "beliefs",
    group: "Who you are",
    title: "Strong beliefs: veganism, climate change, gender-critical views, politics",
    verdict: "maybe",
    characteristic: "Religion or belief, if it is a philosophical belief",
    answer: [
      "A belief is protected if it is a philosophical belief. Tribunals use the test in Grainger plc v Nicholson [2010] ICR 360 (EAT): it must be genuinely held, be a belief rather than an opinion based on the information available, concern a weighty and substantial aspect of human life, have a certain cogency, seriousness, cohesion and importance, and be worthy of respect in a democratic society.",
      "Protecting a belief does not protect every way of expressing it. Employers can act on how a belief is expressed at work, if the response is proportionate.",
    ],
    bestPractice: "Deal with conduct, not the belief, and apply the same standard whichever side of a debate someone is on.",
    law: [{ label: "Equality Act 2010, s.10", url: EQA("section/10") }],
  },
  {
    id: "marriage",
    group: "Who you are",
    title: "Being married, single, engaged or living together",
    verdict: "maybe",
    characteristic: "Marriage and civil partnership (married and civil partners only)",
    answer: [
      "Only being married or in a civil partnership is protected. Being single, engaged or cohabiting is not.",
    ],
    bestPractice: "Don't ask about relationship plans at interview. It helps nobody and invites a sex discrimination claim.",
    law: [
      { label: "Equality Act 2010, s.8", url: EQA("section/8") },
      { label: "Equality Act 2010, s.13(4)", url: EQA("section/13") },
    ],
  },
  {
    id: "association-perception",
    group: "How protection works",
    title: "Being thought to have a characteristic, or linked to someone who has one",
    verdict: "yes",
    characteristic: "Most characteristics",
    answer: [
      "Direct discrimination is about treatment \"because of a protected characteristic\", not only the employee's own. So it covers treating someone badly because people think they are, say, gay or disabled, or because of who they are connected to, such as a disabled child. Harassment works the same way.",
      "The exceptions: pregnancy and maternity at work only protects the woman herself, and marriage and civil partnership only protects the person who is married or a civil partner.",
    ],
    bestPractice: "Ask whether you would have made the same decision if the characteristic, real or assumed, had not been in the picture.",
    law: [
      { label: "Equality Act 2010, s.13", url: EQA("section/13") },
      { label: "Equality Act 2010, s.26", url: EQA("section/26") },
      { label: "Equality Act 2010, s.18", url: EQA("section/18") },
    ],
  },
  {
    id: "complaining",
    group: "How protection works",
    title: "Complaining about discrimination, or helping someone who did",
    verdict: "yes",
    characteristic: "Victimisation (anyone)",
    answer: [
      "Yes, whatever their own characteristics. Treating someone badly because they complained of discrimination, brought or supported a claim, or gave evidence is victimisation. It doesn't matter whether the complaint turns out to be right, unless it was made in bad faith.",
    ],
    bestPractice: "Once someone has complained, check every later decision about them is one you would make anyway, and write down why.",
    law: [{ label: "Equality Act 2010, s.27", url: EQA("section/27") }],
  },
];

export const SITUATION_GROUPS = ["Pregnancy and family", "Health and disability", "Who you are", "How protection works"];

// Guides planned for each hub. A guide with a path is live and linked; one
// without is listed as "being written".
export const EMPLOYER_GUIDES = [
  { title: "Reclaim your family pay", blurb: "How much statutory maternity, paternity and adoption pay comes back through payroll, and how small employers get more than they paid out." },
  { title: "Pregnancy and maternity, done right", blurb: "Risk assessment, time off for antenatal care, the protected period and redundancy." },
  { title: "Mental health: when it's a disability", blurb: "The legal test, adjustments and absence management that holds up." },
  { title: "Neurodiversity at work", blurb: "What it is, how it shows up at work, reasonable adjustments, and when refusing one is justified." },
  { title: "Reasonable adjustments: yes, no and why", blurb: "The duty, what \"reasonable\" means, the business case for saying no, and writing it down." },
  { title: "Menopause at work", blurb: "The legal position, practical adjustments and menopause action plans." },
  { title: "Flexible working requests", blurb: "The day-one right, the business reasons for refusal, and running a fair trial period." },
  { title: "Equal pay and pay gaps", blurb: "Equal pay, gender pay gap reporting, and what is coming on ethnicity and disability." },
  { title: "Recruiting fairly", blurb: "Job adverts, interviews and health questions before an offer." },
];

export const EMPLOYEE_GUIDES = [
  { title: "Pregnant at work", blurb: "Telling your employer, time off for appointments, your risk assessment and the protected period." },
  { title: "Maternity leave and pay", blurb: "What you get, keeping-in-touch days, holiday while you're off, and going back." },
  { title: "Mental health at work", blurb: "When it counts as a disability and how to ask for what helps." },
  { title: "Neurodivergent at work: asking for adjustments", blurb: "What to ask for, how to ask, and what happens if the answer is no." },
  { title: "Asking for flexible working", blurb: "Your day-one right and how to write the request, with a letter template." },
  { title: "Menopause at work", blurb: "What the law covers and what to ask for." },
  { title: "Equal pay: your right to talk about pay", blurb: "Comparing pay with colleagues and what to do if it isn't equal." },
  { title: "Sexual harassment at work", blurb: "What your employer must do to prevent it and how to raise it." },
  { title: "Raising a grievance", blurb: "How and when to do it, with a letter template." },
];

// Free, independent help for employees. No calls from Electra; these are
// other organisations' own services.
export const FREE_HELP = [
  { name: "Acas", url: "https://www.acas.org.uk", what: "Free, impartial advice on workplace rights, and early conciliation, which you must normally contact before making a tribunal claim." },
  { name: "Equality Advisory and Support Service", url: "https://www.equalityadvisoryservice.com", what: "Free advice on discrimination and the Equality Act." },
  { name: "Citizens Advice", url: "https://www.citizensadvice.org.uk/work/", what: "Plain guides to rights at work, and local advisers." },
  { name: "Maternity Action", url: "https://maternityaction.org.uk", what: "Free information on maternity rights at work and benefits." },
];
